/**
 * useCurrentBrand
 * ───────────────
 * Resolves the brand attached to a logged-in brand vendor and returns
 * `{ brandId, brandName, brandLogoUrl, loading }`. For any non-BRAND_OWNER
 * user it returns `{ brandId: null, brandName: null, brandLogoUrl: null,
 * loading: false }` immediately so callers can unconditionally render its
 * output.
 *
 * Why this exists
 * ───────────────
 * The login response does NOT include brand info on the user object —
 * only `_id`, `name`, `operationMode`, etc. Pages that need the brand
 * (currently just BrandProductManagement.js) each do their own
 * `GET /api/brands?vendorId={userId}` fetch. Extracting that into a
 * shared hook lets the Sidebar render "Nile Chocolates" instead of the
 * operator's personal name, without touching the backend login
 * contract, and keeps the fetch logic DRY for future callers.
 *
 * Caching
 * ───────
 * The resolved brand is cached in a module-level variable keyed by
 * userId so remounting the Sidebar (or mounting a second consumer on
 * the same page) does NOT re-hit the API. The cache is invalidated
 * automatically when the logged-in userId changes — so logging out +
 * in as a different brand refetches correctly. `clearCurrentBrandCache`
 * is exported for any caller that needs to force-refresh (e.g. after
 * editing the brand's tradingName).
 *
 * Field order
 * ───────────
 * Name: mirrors the existing resolver in BrandProductManagement.js —
 *   tradingName → name → "Brand"
 * so the sidebar + the page banner always agree on which string to show.
 * Logo: `imageUrl` is the canonical Brand model field (see
 * `models/brandModel.js` + BrandManagement.js, which renders
 * `<img src={brand.imageUrl} />`).
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";

const API_URL = process.env.REACT_APP_SERVER_URL;

// Module-level cache — keyed by userId so a login/logout cycle into a
// different account does not leak the previous brand.
let _cache = { userId: null, brandId: null, brandName: null, brandLogoUrl: null };

export const clearCurrentBrandCache = () => {
  _cache = { userId: null, brandId: null, brandName: null, brandLogoUrl: null };
};

export default function useCurrentBrand() {
  const user = getUserFromLocalStorage();
  const userId = user?.result?._id;
  const operationMode = user?.result?.operationMode;
  const isBrandOwner = operationMode === "BRAND_OWNER";

  // Seed state from cache when available so the first render of the
  // Sidebar doesn't flicker from "Guest" → brand name on reloads.
  const cached = isBrandOwner && _cache.userId === userId ? _cache : null;

  const [brandId, setBrandId] = useState(cached?.brandId || null);
  const [brandName, setBrandName] = useState(cached?.brandName || null);
  const [brandLogoUrl, setBrandLogoUrl] = useState(cached?.brandLogoUrl || null);
  const [loading, setLoading] = useState(isBrandOwner && !cached);

  useEffect(() => {
    // Not a brand vendor → nothing to resolve.
    if (!isBrandOwner || !userId) {
      setBrandId(null);
      setBrandName(null);
      setBrandLogoUrl(null);
      setLoading(false);
      return;
    }
    // Already cached for this user → use it and skip the fetch.
    if (_cache.userId === userId && _cache.brandName) {
      setBrandId(_cache.brandId);
      setBrandName(_cache.brandName);
      setBrandLogoUrl(_cache.brandLogoUrl);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const token = getTokenFromLocalStorage();
    axios
      .get(`${API_URL}/api/brands`, {
        params: { vendorId: userId },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        if (cancelled) return;
        const brands = res.data?.data || res.data || [];
        const first = Array.isArray(brands) ? brands[0] : null;
        const resolvedId = first ? String(first._id) : null;
        // Fallback chain matches BrandProductManagement.js so the
        // sidebar and the page banner never disagree on the brand's
        // display name.
        const resolvedName = first
          ? first.tradingName || first.name || null
          : null;
        // `imageUrl` is the Brand model's logo field (see brandModel.js).
        // Null when the brand hasn't uploaded a logo yet — Sidebar falls
        // back to the avatar initial in that case.
        const resolvedLogo = first?.imageUrl || null;
        _cache = {
          userId,
          brandId: resolvedId,
          brandName: resolvedName,
          brandLogoUrl: resolvedLogo,
        };
        setBrandId(resolvedId);
        setBrandName(resolvedName);
        setBrandLogoUrl(resolvedLogo);
      })
      .catch(() => {
        // Silent failure — the consumer (Sidebar) already has a
        // fallback chain ending in "Guest", so an offline/error state
        // just degrades to the user's personal name. No toast here
        // because this hook is mounted on every page.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, isBrandOwner]);

  return { brandId, brandName, brandLogoUrl, loading };
}
